import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiShield, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Modal, Button, Form } from 'react-bootstrap';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionsMap, setPermissionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [permissionFilter, setPermissionFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const { selectedHierarchy } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: []
  });

  useEffect(() => {
    loadPermissions();
    loadRoles();
  }, [selectedHierarchy]);

  const loadPermissions = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      const permissionsRef = collection(db, 'permissions');
      const permissionsQuery = query(
        permissionsRef,
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(permissionsQuery);
      const permissionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Remove duplicates based on permission name
      const uniquePermissions = [];
      const seenNames = new Set();
      
      permissionsData.forEach(perm => {
        const permName = (perm.name || perm.label || perm.id).toLowerCase();
        if (!seenNames.has(permName)) {
          seenNames.add(permName);
          uniquePermissions.push(perm);
        }
      });
      
      // Sort permissions alphabetically by name
      uniquePermissions.sort((a, b) => {
        const aName = (a.name || a.label || a.id).toLowerCase();
        const bName = (b.name || b.label || b.id).toLowerCase();
        return aName.localeCompare(bName);
      });
      
      setPermissions(uniquePermissions);
      
      // Create a map for quick lookup
      const map = {};
      permissionsData.forEach(perm => {
        map[perm.id] = perm.name || perm.label || perm.id;
      });
      setPermissionsMap(map);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const loadRoles = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    try {
      setLoading(true);
      const rolesQuery = query(
        collection(db, 'roles'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(rolesQuery);
      const rolesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Verify outlet is selected
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    try {
      const roleData = {
        ...formData,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };

      if (editingRole) {
        // SECURITY: Verify the role belongs to the current outlet before updating
        if (editingRole.outletId !== selectedHierarchy.outlet.id) {
          alert('Security Error: Cannot modify role from another outlet.');
          setShowModal(false);
          resetForm();
          return;
        }
        await updateDoc(doc(db, 'roles', editingRole.id), roleData);
      } else {
        roleData.createdAt = new Date();
        await addDoc(collection(db, 'roles'), roleData);
      }

      setShowModal(false);
      resetForm();
      loadRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      alert('Failed to save role. Please try again.');
    }
  };

  const handleEdit = (role) => {
    // SECURITY: Verify the role belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (role.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot edit role from another outlet.');
      return;
    }
    
    setEditingRole(role);
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissionIds: role.permissionIds || []
    });
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    // SECURITY: Verify the role belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (role.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot delete role from another outlet.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      try {
        await deleteDoc(doc(db, 'roles', role.id));
        loadRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        alert('Failed to delete role.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissionIds: []
    });
    setEditingRole(null);
  };

  const togglePermission = (permissionId) => {
    setFormData({
      ...formData,
      permissionIds: formData.permissionIds.includes(permissionId)
        ? formData.permissionIds.filter(id => id !== permissionId)
        : [...formData.permissionIds, permissionId]
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredRoles = roles.filter(role => {
    // Search filter
    const matchesSearch = role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         role.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'system' && !role.isSystem) return false;
      if (typeFilter === 'custom' && role.isSystem) return false;
    }
    
    // Permission filter
    if (permissionFilter !== 'all') {
      if (!role.permissionIds || !role.permissionIds.includes(permissionFilter)) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    
    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortField === 'description') {
      aVal = (a.description || '').toLowerCase();
      bVal = (b.description || '').toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const countPermissions = (permissionIds) => {
    if (!permissionIds || !Array.isArray(permissionIds)) return 0;
    return permissionIds.length;
  };

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Roles</h1>
        <p className="page-subtitle">Manage user roles and access control</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Roles</p>
                  <h3 className="mb-0">{roles.length}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiShield className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">System Roles</p>
                  <h3 className="mb-0 text-success">
                    {roles.filter(r => r.isSystem).length}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-gear text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Custom Roles</p>
                  <h3 className="mb-0 text-primary">
                    {roles.filter(r => !r.isSystem).length}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-person-badge text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Roles Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Roles</h5>
          <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus className="me-2" />
            Add Role
          </button>
        </div>

        {/* Filter Controls */}
        {!loading && roles.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Type</label>
                <select 
                  className="form-select form-select-sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="system">System Roles</option>
                  <option value="custom">Custom Roles</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Permission</label>
                <select 
                  className="form-select form-select-sm"
                  value={permissionFilter}
                  onChange={(e) => setPermissionFilter(e.target.value)}
                >
                  <option value="all">All Permissions</option>
                  {permissions.map(permission => (
                    <option key={permission.id} value={permission.id}>
                      {permission.name || permission.label || permission.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setSearchTerm('');
                    setTypeFilter('all');
                    setPermissionFilter('all');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              </div>
            </div>
            {(searchTerm || typeFilter !== 'all' || permissionFilter !== 'all') && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredRoles.length} of {roles.length} roles
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading roles...</p>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-5">
              <FiShield size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm || typeFilter !== 'all' || permissionFilter !== 'all'
                  ? 'No roles match your filters.'
                  : 'No roles found.'}
              </p>
              {(searchTerm || typeFilter !== 'all' || permissionFilter !== 'all') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setTypeFilter('all');
                    setPermissionFilter('all');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Role Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('description')}
                    >
                      Description
                      {sortField === 'description' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th>Permissions</th>
                    <th>Type</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.map(role => (
                    <tr key={role.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              minWidth: '40px',
                              minHeight: '40px',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}
                          >
                            <FiShield size={20} />
                          </div>
                          <span className="fw-semibold">{role.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="text-muted">{role.description || 'No description'}</span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {role.permissionIds && Array.isArray(role.permissionIds) && role.permissionIds.length > 0 ? (
                            role.permissionIds.map(permissionId => {
                              const permissionName = permissionsMap[permissionId] || permissionId;
                              return (
                                <span key={permissionId} className="badge bg-primary">
                                  {permissionName}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-muted small">No permissions</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {role.isSystem ? (
                          <span className="badge bg-secondary">System</span>
                        ) : (
                          <span className="badge bg-info">Custom</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(role)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          {!role.isSystem && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(role)}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRole ? 'Edit Role' : 'Add New Role'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Role Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Label className="fw-semibold mb-3">Permissions</Form.Label>
                {permissions.length === 0 ? (
                  <p className="text-muted">Loading permissions...</p>
                ) : (
                  <div className="row g-2">
                    {permissions.map(permission => (
                      <div key={permission.id} className="col-md-6">
                        <Form.Check
                          type="checkbox"
                          id={`perm-${permission.id}`}
                          label={permission.name || permission.label || permission.id}
                          checked={formData.permissionIds.includes(permission.id)}
                          onChange={() => togglePermission(permission.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingRole ? 'Update' : 'Add'} Role
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
